// Multi-currency display for B2B pharma buyers.
//
// Why this matters: catalogue prices are stored USD-only in the DB
// (products.price.value is per-gram USD). LATAM + EU + APAC procurement
// teams mentally convert every quote before deciding whether to inquire;
// showing the price in their local currency removes that friction and
// signals "we understand your market." The conversion audit flagged this
// as a Phase-3 lever.
//
// FX strategy: STATIC rates updated occasionally (this file). Live FX
// would require a daily fetch from an open API (exchangerate.host,
// frankfurter.app, etc.) — fine to add later but unnecessary for
// catalogue display where prices are quoted in USD anyway and the local-
// currency display is purely indicative ("approximately €{x}").
//
// The actual quote/order amount stays USD. The displayed local-currency
// number is a UX hint, not a contractual price — same approach Amazon
// uses for international shipping previews.

export type CurrencyCode = "USD" | "EUR" | "GBP" | "INR" | "JPY" | "CNY" | "BRL" | "MXN" | "AUD" | "AED";

export interface CurrencyInfo {
  code: CurrencyCode;
  symbol: string;
  // Approximate USD-to-target rate. Update periodically — this is for
  // display only; commercial quotes are issued in USD with the customer's
  // bank handling settlement FX. Last updated: 2026-07-17.
  perUsd: number;
  // Where to put the symbol. "before" = "$1.50", "after" = "1.50 €".
  position: "before" | "after";
  // Locale used for the number-formatting (thousand separator, decimal).
  locale: string;
  // Regions where this currency is preferred (used for auto-detection).
  preferredIn: string[];
}

/* Rates refreshed 2026-07-17 against a mid-market feed. Because this
   is display-only (commercial quotes ship in USD, customer bank handles
   settlement FX), being within ~2% of true mid-market is fine. Refresh
   when any rate drifts more than 5% or every ~90 days, whichever
   comes first. */
export const CURRENCIES: Record<CurrencyCode, CurrencyInfo> = {
  USD: { code: "USD", symbol: "$",  perUsd: 1.0,    position: "before", locale: "en-US",  preferredIn: ["US", "EC", "PA", "SV"] },
  EUR: { code: "EUR", symbol: "€",  perUsd: 0.86,   position: "after",  locale: "de-DE",  preferredIn: ["DE", "FR", "IT", "ES", "NL", "BE", "AT", "IE", "PT", "FI", "GR"] },
  GBP: { code: "GBP", symbol: "£",  perUsd: 0.74,   position: "before", locale: "en-GB",  preferredIn: ["GB"] },
  INR: { code: "INR", symbol: "₹",  perUsd: 86.0,   position: "before", locale: "en-IN",  preferredIn: ["IN"] },
  JPY: { code: "JPY", symbol: "¥",  perUsd: 148.0,  position: "before", locale: "ja-JP",  preferredIn: ["JP"] },
  CNY: { code: "CNY", symbol: "¥",  perUsd: 7.18,   position: "before", locale: "zh-CN",  preferredIn: ["CN"] },
  BRL: { code: "BRL", symbol: "R$", perUsd: 5.55,   position: "before", locale: "pt-BR",  preferredIn: ["BR"] },
  MXN: { code: "MXN", symbol: "$",  perUsd: 18.7,   position: "before", locale: "es-MX",  preferredIn: ["MX"] },
  AUD: { code: "AUD", symbol: "A$", perUsd: 1.49,   position: "before", locale: "en-AU",  preferredIn: ["AU"] },
  AED: { code: "AED", symbol: "د.إ", perUsd: 3.67,  position: "after",  locale: "ar-AE",  preferredIn: ["AE", "SA", "QA", "BH", "OM", "KW"] },
};

export const CURRENCY_LIST: CurrencyCode[] = ["USD", "EUR", "GBP", "INR", "JPY", "CNY", "BRL", "MXN", "AUD", "AED"];

const STORAGE_KEY = "longshine-currency";

/**
 * Persisted currency preference for this visitor. Falls back to an
 * IANA-timezone heuristic on first visit so e.g. a buyer in
 * Europe/Berlin sees EUR by default. Persisted as a plain string
 * (the code) in localStorage so it survives session/tab close.
 */
export const detectInitialCurrency = (): CurrencyCode => {
  if (typeof window === "undefined") return "USD";
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as CurrencyCode | null;
    if (stored && CURRENCIES[stored]) return stored;
  } catch { /* private mode */ }
  // Timezone heuristic: map common pharma-buyer timezones to currency.
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz.startsWith("Europe/")) {
      if (tz.includes("London")) return "GBP";
      return "EUR";
    }
    if (tz.startsWith("Asia/Tokyo")) return "JPY";
    if (tz.startsWith("Asia/Shanghai") || tz.startsWith("Asia/Hong_Kong")) return "CNY";
    if (tz.startsWith("Asia/Kolkata") || tz.startsWith("Asia/Calcutta") || tz.startsWith("Asia/Mumbai")) return "INR";
    if (tz === "America/Sao_Paulo" || tz.startsWith("America/Sao_Paulo")) return "BRL";
    if (tz.startsWith("America/Mexico")) return "MXN";
    if (tz.startsWith("Australia/")) return "AUD";
    if (tz.startsWith("Asia/Dubai") || tz.startsWith("Asia/Riyadh")) return "AED";
  } catch { /* Intl not supported */ }
  return "USD";
};

export const setCurrency = (code: CurrencyCode) => {
  try { localStorage.setItem(STORAGE_KEY, code); } catch { /* private mode */ }
};

/**
 * Format a USD-denominated value in the chosen currency. The CHOSEN
 * currency may be USD itself, in which case this just formats USD.
 *
 * Convention: pharmaceutical price-per-gram displays use 2 decimal
 * places (so $0.60/g remains $0.60/g, never $1). JPY and INR use 0 or 1
 * decimal because their fractional unit isn't meaningful at retail.
 */
export const formatCurrency = (
  usdValue: number,
  code: CurrencyCode,
  options: { compact?: boolean } = {},
): string => {
  const info = CURRENCIES[code] || CURRENCIES.USD;
  const converted = usdValue * info.perUsd;
  // Decimals: JPY/CNY -> 0 (yen has no sub-unit at typical price points);
  // INR -> 1 (₹83.50 is meaningful); everything else -> 2.
  const decimals = code === "JPY" ? 0 : code === "CNY" ? 1 : code === "INR" ? 1 : 2;
  const formatter = new Intl.NumberFormat(info.locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    notation: options.compact ? "compact" : "standard",
  });
  const num = formatter.format(converted);
  return info.position === "before" ? `${info.symbol}${num}` : `${num} ${info.symbol}`;
};
